import React, { useState, useEffect } from "react";
import { Category } from "../../services/categoryService";
import { ProductFilters as ProductFiltersType } from "../../services/productService";

interface ProductFiltersProps {
  categories: Category[];
  filters: ProductFiltersType;
  onFiltersChange: (
    filters: ProductFiltersType,
    shouldNavigate?: boolean
  ) => void;
}

interface FilterSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  isOpen,
  onToggle,
  children,
}) => {
  return (
    <div className="border-b border-slate-200 dark:border-slate-700 py-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="font-semibold text-[#0d171b] dark:text-white text-base">
          {title}
        </span>
        <svg
          className={`w-5 h-5 text-slate-500 dark:text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && <div className="mt-4 space-y-3">{children}</div>}
    </div>
  );
};

const ProductFilters: React.FC<ProductFiltersProps> = ({
  categories,
  filters,
  onFiltersChange,
}) => {
  const [searchText, setSearchText] = useState(filters.search || "");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    if (Array.isArray(filters.categoryId)) {
      return filters.categoryId;
    } else if (filters.categoryId) {
      return [filters.categoryId];
    }
    return [];
  });
  const [minPrice, setMinPrice] = useState<string>(
    filters.minPrice?.toString() || ""
  );
  const [maxPrice, setMaxPrice] = useState<string>(
    filters.maxPrice?.toString() || ""
  );

  // Sync local state when filters prop changes (e.g., when clearFilters is called from parent)
  useEffect(() => {
    if (Array.isArray(filters.categoryId)) {
      setSelectedCategories(filters.categoryId);
    } else if (filters.categoryId) {
      setSelectedCategories([filters.categoryId]);
    } else {
      setSelectedCategories([]);
    }
    setSearchText(filters.search || "");
    setMinPrice(filters.minPrice?.toString() || "");
    setMaxPrice(filters.maxPrice?.toString() || "");
  }, [filters.categoryId, filters.search, filters.minPrice, filters.maxPrice]);

  const [sectionsOpen, setSectionsOpen] = useState({
    category: false,
    price: false,
  });

  const toggleSection = (section: "category" | "price") => {
    setSectionsOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const countActiveFilters = () => {
    let count = 0;
    if (selectedCategories.length > 0) count += selectedCategories.length;
    if (minPrice) count++;
    if (maxPrice) count++;
    if (searchText) count++;
    return count;
  };

  const applyFilters = () => {
    const newFilters: ProductFiltersType = {
      page: 1,
      limit: filters.limit || 20,
    };

    if (selectedCategories.length > 0) {
      newFilters.categoryId = selectedCategories;
    }

    if (searchText.trim()) {
      newFilters.search = searchText.trim();
    }

    if (minPrice) {
      newFilters.minPrice = parseFloat(minPrice);
    }

    if (maxPrice) {
      newFilters.maxPrice = parseFloat(maxPrice);
    }

    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSearchText("");
    setMinPrice("");
    setMaxPrice("");
    // Clear search timeout if exists
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
    // Reset to page 1 and clear all filters, trigger navigation
    onFiltersChange(
      {
        page: 1,
        limit: filters.limit || 20,
        categoryId: undefined,
        search: undefined,
        minPrice: undefined,
        maxPrice: undefined,
      },
      true
    );
  };

  const activeFiltersCount = countActiveFilters();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <div className="product-filters bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
      {/* Header with Active Filters Badge */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-[#0d171b] dark:text-white">
          الفلاتر
        </h3>
        {activeFiltersCount > 0 && (
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">
            {activeFiltersCount}
          </span>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          البحث
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchText}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);

              // Debounce search - apply after 500ms of no typing
              if (searchTimeout) {
                clearTimeout(searchTimeout);
              }

              const timeout = setTimeout(() => {
                const newFilters: ProductFiltersType = {
                  ...filters,
                  search: value.trim() || undefined,
                  page: 1,
                };
                onFiltersChange(newFilters);
              }, 500);

              setSearchTimeout(timeout);
            }}
            placeholder="ابحث عن منتج..."
            className="w-full rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 h-12 px-4 pr-12 text-base transition"
          />
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className="w-5 h-5 text-slate-400 dark:text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          {searchText && (
            <button
              onClick={() => {
                setSearchText("");
                if (searchTimeout) {
                  clearTimeout(searchTimeout);
                }
                const newFilters: ProductFiltersType = {
                  ...filters,
                  search: undefined,
                  page: 1,
                };
                onFiltersChange(newFilters);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Category Filter Section */}
      <FilterSection
        title={`الفئة ${
          selectedCategories.length > 0 ? `(${selectedCategories.length})` : ""
        }`}
        isOpen={sectionsOpen.category}
        onToggle={() => toggleSection("category")}
      >
        {categories.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            لا توجد فئات متاحة
          </p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
            {categories.map((category) => (
              <label
                key={category.id}
                className="flex items-center cursor-pointer group p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition"
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600 text-primary focus:ring-primary focus:ring-2 cursor-pointer transition"
                />
                <span className="mr-3 text-[#0d171b] dark:text-white group-hover:text-primary transition font-medium">
                  {category.name}
                </span>
                {selectedCategories.includes(category.id) && (
                  <svg
                    className="w-4 h-4 text-primary mr-auto"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </label>
            ))}
          </div>
        )}
      </FilterSection>

      {/* Price Range Filter Section */}
      <FilterSection
        title={`السعر ${minPrice || maxPrice ? "(محدد)" : ""}`}
        isOpen={sectionsOpen.price}
        onToggle={() => toggleSection("price")}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              الحد الأدنى (د.ج)
            </label>
            <div className="relative">
              <input
                type="number"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="0"
                min="0"
                step="100"
                className="w-full rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 h-11 px-4 text-sm transition"
              />
              {minPrice && (
                <button
                  onClick={() => setMinPrice("")}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              الحد الأقصى (د.ج)
            </label>
            <div className="relative">
              <input
                type="number"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="100000"
                min="0"
                step="100"
                className="w-full rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 h-11 px-4 text-sm transition"
              />
              {maxPrice && (
                <button
                  onClick={() => setMaxPrice("")}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {(minPrice || maxPrice) && (
            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {minPrice && maxPrice
                  ? `من ${parseFloat(
                      minPrice
                    ).toLocaleString()} إلى ${parseFloat(
                      maxPrice
                    ).toLocaleString()} د.ج`
                  : minPrice
                  ? `من ${parseFloat(minPrice).toLocaleString()} د.ج`
                  : `حتى ${parseFloat(maxPrice).toLocaleString()} د.ج`}
              </p>
            </div>
          )}
        </div>
      </FilterSection>

      {/* Apply Filters Button */}
      <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
        <button
          onClick={applyFilters}
          disabled={activeFiltersCount === 0}
          className="w-full rounded-lg bg-primary text-white font-bold hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed h-12 px-4 text-base transition shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30"
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            تطبيق الفلاتر
            {activeFiltersCount > 0 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
                {activeFiltersCount}
              </span>
            )}
          </span>
        </button>
        {activeFiltersCount > 0 && (
          <button
            onClick={clearFilters}
            className="w-full rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white hover:bg-slate-50 dark:hover:bg-slate-700 h-10 px-4 text-sm font-semibold transition flex items-center justify-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            مسح جميع الفلاتر
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductFilters;
